import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, Search, User } from 'lucide-react';

interface Entity {
  id: string;
  name: string;
  profile_picture: string | null;
  profile_picture_url?: string | null; // Support both formats
}

interface EntitySelectorProps {
  entities: Entity[];
  selectedEntityId: string;
  onSelect: (entityId: string) => void;
  placeholder?: string;
  isLoading?: boolean;
}

const EntitySelector: React.FC<EntitySelectorProps> = ({
  entities,
  selectedEntityId,
  onSelect,
  placeholder = "Selecione um perfil",
  isLoading = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);

  const selectedEntity = entities.find(entity => entity.id === selectedEntityId);

  const filteredEntities = entities.filter(entity =>
    entity.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getEntityImage = (entity: Entity) => {
    return entity.profile_picture || entity.profile_picture_url;
  };

  const handleSelect = (entityId: string) => {
    onSelect(entityId);
    setIsOpen(false);
    setSearchTerm('');
  };

  const handleToggle = () => {
    if (!isLoading) {
      setIsOpen(!isOpen);
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Selected Entity Display */}
      <div
        onClick={handleToggle}
        className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brown-500 focus:border-transparent cursor-pointer transition-colors ${
          isLoading ? 'opacity-50 cursor-not-allowed' : 'hover:border-gray-400'
        }`}
      >
        {isLoading ? (
          <div className="flex items-center">
            <div className="text-sm text-gray-500">Carregando perfis...</div>
          </div>
        ) : selectedEntity ? (
          <div className="flex items-center space-x-3">
            {getEntityImage(selectedEntity) ? (
              <img
                src={getEntityImage(selectedEntity)!}
                alt={selectedEntity.name}
                className="w-8 h-8 rounded-full object-cover"
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.style.display = 'none';
                  target.nextElementSibling?.classList.remove('hidden');
                }}
              />
            ) : null}
            <div className={`w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center ${getEntityImage(selectedEntity) ? 'hidden' : ''}`}>
              <User className="w-4 h-4 text-gray-500" />
            </div>
            <span className="flex-1 text-gray-900">{selectedEntity.name}</span>
            <ChevronDown className={`w-5 h-5 text-gray-500 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
          </div>
        ) : (
          <div className="flex items-center justify-between">
            <span className="text-gray-500">{placeholder}</span>
            <ChevronDown className={`w-5 h-5 text-gray-500 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
          </div>
        )}
      </div>

      {/* Dropdown */}
      {isOpen && !isLoading && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-hidden">
          {/* Search Input */}
          <div className="p-2 border-b border-gray-200">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Buscar perfil..."
                className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-brown-500 focus:border-transparent"
                autoFocus
              />
            </div>
          </div>

          {/* Entity List */}
          <div className="max-h-48 overflow-y-auto">
            {filteredEntities.length === 0 ? (
              <div className="p-3 text-sm text-gray-500 text-center">
                {searchTerm ? 'Nenhum perfil encontrado' : 'Nenhum perfil disponível'}
              </div>
            ) : (
              filteredEntities.map((entity) => (
                <div
                  key={entity.id}
                  onClick={() => handleSelect(entity.id)}
                  className={`flex items-center space-x-3 p-3 cursor-pointer transition-colors ${
                    entity.id === selectedEntityId
                      ? 'bg-brown-50 text-brown-900'
                      : 'hover:bg-gray-50'
                  }`}
                >
                  {getEntityImage(entity) ? (
                    <img
                      src={getEntityImage(entity)!}
                      alt={entity.name}
                      className="w-8 h-8 rounded-full object-cover"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.style.display = 'none';
                        target.nextElementSibling?.classList.remove('hidden');
                      }}
                    />
                  ) : null}
                  <div className={`w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center ${getEntityImage(entity) ? 'hidden' : ''}`}>
                    <User className="w-4 h-4 text-gray-500" />
                  </div>
                  <span className="flex-1 text-sm">{entity.name}</span>
                  {entity.id === selectedEntityId && (
                    <div className="w-2 h-2 bg-brown-600 rounded-full"></div>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default EntitySelector;